import { theme } from './theme';
import { Button } from './ui';

export type PlanInterval = 'monthly' | 'yearly' | 'lifetime';

interface PlanDef {
  interval: PlanInterval;
  name: string;
  badge?: string;
  priceValue: string;
  pricePeriod: string;
  features: string[];
  ctaLabel: string;
  featured?: boolean;
}

const PLANS: PlanDef[] = [
  {
    interval: 'monthly',
    name: 'Monthly',
    priceValue: '7',
    pricePeriod: '/month',
    features: ['Same 5 GB vault', 'Anonymous account support', 'Cancel anytime'],
    ctaLabel: 'Choose monthly',
  },
  {
    interval: 'lifetime',
    name: 'Lifetime',
    badge: 'Preferred',
    priceValue: '300',
    pricePeriod: 'once',
    features: ['No recurring billing', 'Same 5 GB vault', 'Future delivery included'],
    ctaLabel: 'Choose lifetime',
    featured: true,
  },
  {
    interval: 'yearly',
    name: 'Yearly',
    priceValue: '5',
    pricePeriod: '/month',
    features: ['Save compared to monthly', 'Same 5 GB vault', 'Delivery stays active'],
    ctaLabel: 'Choose yearly',
  },
];

export function SubscriptionPlans({
  currentInterval,
  onSelect,
  busyInterval,
}: {
  currentInterval?: string | null;
  onSelect: (interval: PlanInterval) => void;
  busyInterval?: PlanInterval | null;
}) {
  return (
    <>
      <style>{`
        .sp-plans-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }
        @media (max-width: 980px) {
          .sp-plans-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="sp-plans-grid">
        {PLANS.map((plan) => {
          const isCurrent = currentInterval === plan.interval;
          const isBusy = busyInterval === plan.interval;
          const disabled = isCurrent || isBusy || !!busyInterval;

          return (
            <article
              key={plan.interval}
              style={{
                position: 'relative',
                padding: 28,
                borderRadius: theme.radius.md,
                border: plan.featured
                  ? '1px solid rgba(210,166,103,0.36)'
                  : `1px solid ${theme.colors.line}`,
                background: plan.featured
                  ? 'linear-gradient(180deg, rgba(210,166,103,0.06), rgba(210,166,103,0.02))'
                  : 'linear-gradient(180deg, rgba(18,21,27,0.86), rgba(8,10,13,0.84))',
                boxShadow: theme.shadow.soft,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 280,
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: theme.colors.muted,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}>
                  {plan.name}
                </div>
                {plan.badge && (
                  <div style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: 999,
                    background: 'rgba(210,166,103,0.14)',
                    border: '1px solid rgba(210,166,103,0.32)',
                    color: theme.colors.gold,
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: '0.8px',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}>
                    {plan.badge}
                  </div>
                )}
              </div>

              <div style={{
                marginTop: 14,
                display: 'flex',
                alignItems: 'baseline',
                gap: 2,
              }}>
                <span style={{
                  fontSize: 18,
                  fontWeight: 500,
                  color: theme.colors.muted,
                }}>$</span>
                <span style={{
                  fontSize: 52,
                  fontWeight: 600,
                  lineHeight: 1,
                  letterSpacing: '-2px',
                }}>
                  {plan.priceValue}
                </span>
                <span style={{
                  fontSize: 13,
                  color: theme.colors.muted,
                  marginLeft: 4,
                }}>
                  {plan.pricePeriod}
                </span>
              </div>

              <ul style={{
                display: 'grid',
                gap: 7,
                margin: '20px 0 24px',
                padding: 0,
                listStyle: 'none',
                color: 'rgba(245,245,247,0.62)',
                fontSize: 13,
                lineHeight: 1.5,
              }}>
                {plan.features.map((f) => (
                  <li key={f}>
                    <span style={{ color: 'rgba(210,166,103,0.75)', marginRight: 7 }}>—</span>
                    {f}
                  </li>
                ))}
              </ul>

              <div style={{ marginTop: 'auto' }}>
                {plan.featured ? (
                  <Button
                    onClick={() => onSelect(plan.interval)}
                    disabled={disabled}
                    style={{ width: '100%', minHeight: 46, justifyContent: 'center', fontSize: 14 }}
                  >
                    {isCurrent ? 'Current' : isBusy ? 'Activating…' : plan.ctaLabel}
                  </Button>
                ) : (
                  <button
                    onClick={() => onSelect(plan.interval)}
                    disabled={disabled}
                    style={{
                      appearance: 'none',
                      cursor: disabled ? 'default' : 'pointer',
                      display: 'block',
                      textAlign: 'center',
                      width: '100%',
                      padding: '13px 20px',
                      borderRadius: theme.radius.sm,
                      border: '1px solid rgba(245,245,247,0.16)',
                      background: 'rgba(255,255,255,0.03)',
                      color: theme.colors.text,
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      transition: 'all 0.25s',
                      opacity: disabled && !isCurrent ? 0.55 : 1,
                    }}
                  >
                    {isCurrent ? 'Current' : isBusy ? 'Activating…' : plan.ctaLabel}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}
